// <pos-tenant-setup/> — 首次登入後建立店家 (tenant) + owner profile
// 流程：user 有 session 但無 tenant_id → 顯示本元件 → 呼叫 /api/auth-tenant-provision → 強制刷新 JWT
// see architecture.md#L280 (auth API)

import { getSupabase } from '../lib/supabase-client.js';
import { apiUrl } from '../lib/api-base.js';

class PosTenantSetup extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this.render();
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host { display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: var(--s-4); }
        .card {
          max-width: 440px; width: 100%;
          padding: var(--s-8);
          background: var(--bg-elevated);
          border: 1px solid var(--border);
          border-radius: var(--r-lg);
        }
        h1 { font-family: var(--font-display); color: var(--brand-primary); margin-bottom: var(--s-3); font-size: 1.6rem; }
        .sub { color: var(--fg-muted); margin-bottom: var(--s-6); font-size: 0.9rem; line-height: 1.5; }
        label { display: block; margin: var(--s-4) 0 var(--s-1); font-size: 0.85rem; color: var(--fg-muted); }
        input { width: 100%; padding: var(--s-3); }
        .hint { color: var(--fg-muted); font-size: 0.75rem; margin-top: var(--s-1); }
        button { width: 100%; margin-top: var(--s-5); padding: var(--s-3); }
        .msg { margin-top: var(--s-4); padding: var(--s-3); border-radius: var(--r-md); }
        .msg.err { background: rgba(220, 38, 38, 0.1); color: var(--danger); }
        .msg.info { background: rgba(59, 130, 246, 0.1); color: var(--fg); }
      </style>
      <div class="card">
        <h1>建立你的店家</h1>
        <p class="sub">第一次登入需要設定店家基本資料，之後才能開始使用 POS。</p>
        <form id="f">
          <label for="display_name">店家名稱</label>
          <input id="display_name" name="display_name" type="text" required maxlength="50" placeholder="例：老婆咖啡" class="input" />

          <label for="slug">店家代稱（網址用）</label>
          <input id="slug" name="slug" type="text" required minlength="3" maxlength="30" pattern="[a-z0-9-]+" placeholder="例：laopo-coffee" class="input" />
          <div class="hint">英文小寫、數字、連字號。設定後不能改。</div>

          <button type="submit" class="btn btn-primary" id="submit-btn">建立店家</button>
          <div id="msg"></div>
        </form>
      </div>
    `;

    this.shadowRoot.querySelector('#f').addEventListener('submit', (e) => this.onSubmit(e));
    this.shadowRoot.querySelector('#display_name').addEventListener('input', (e) => this.autoSlug(e));
  }

  autoSlug(e) {
    const slugInput = this.shadowRoot.querySelector('#slug');
    if (slugInput.dataset.manuallyEdited === 'true') return;
    slugInput.value = e.target.value
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .slice(0, 30);
  }

  async onSubmit(e) {
    e.preventDefault();
    const btn = this.shadowRoot.querySelector('#submit-btn');
    const msg = this.shadowRoot.querySelector('#msg');
    const display_name = e.target.display_name.value.trim();
    const slug = e.target.slug.value.trim();

    if (!/^[a-z0-9-]+$/.test(slug)) {
      msg.className = 'msg err';
      msg.textContent = '代稱只能用英文小寫、數字、連字號';
      return;
    }

    btn.disabled = true;
    msg.className = 'msg info';
    msg.textContent = '建立中...';

    try {
      const supabase = getSupabase();
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      if (!token) throw new Error('請重新登入');

      const res = await fetch(apiUrl('/api/auth-tenant-provision'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'Idempotency-Key': crypto.randomUUID(),
        },
        body: JSON.stringify({ display_name, slug }),
      });
      const body = await res.json();
      if (!res.ok) {
        const code = body?.error?.code;
        if (code === 'slug_taken') throw new Error('此代稱已被使用，請換一個');
        if (code === 'already_provisioned') {
          msg.className = 'msg info';
          msg.textContent = '店家已設定過，重新載入中...';
          await supabase.auth.refreshSession();
          location.reload();
          return;
        }
        throw new Error(body?.error?.message_zh || '建立失敗');
      }

      msg.className = 'msg info';
      msg.textContent = '✓ 建立成功，載入中...';

      // 強制刷新 JWT → 新 token 會包含 tenant_id 與 role=owner
      await supabase.auth.refreshSession();
      location.hash = '#/dashboard';
      location.reload();
    } catch (err) {
      msg.className = 'msg err';
      msg.textContent = `✗ ${err.message}`;
      btn.disabled = false;
    }
  }
}

customElements.define('pos-tenant-setup', PosTenantSetup);
